'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import ConfirmModal from '@/components/common/ConfirmModal';
import { useGlobal } from '@/contexts/GlobalContext';

interface ConfirmModalContextProps {
  isOpen: boolean;
  openConfirmModal: (content: ReactNode) => void;
  closeConfirmModal: () => void;
}

const ConfirmModalContext = createContext<ConfirmModalContextProps | undefined>(undefined);

export const ConfirmModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = useGlobal();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [modalComponent, setModalComponent] = useState<React.ReactNode | null>(null);

  const openConfirmModal = (component: React.ReactNode) => {
    setModalComponent(component);
    setIsOpen(true);
  };

  const closeConfirmModal = () => {
    setModalComponent(null);
    setIsOpen(false);
  };

  useEffect(() => {
    // NOTE: スクロールバーのレイアウトシフト対応
    // NOTE: ドロワーやモーダル要素が表示されていない（重なっていない）場合に初期化する
    if (context.overlapLevel === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable');
    }
  }, [context.overlapLevel]);

  useEffect(() => {
    if (isOpen) {
      context.handleIncrementOverlapLevel();

      // NOTE: スクロールバーのレイアウトシフト対応
      // NOTE: ドロワーやモーダル要素が表示する（重なる）場合にスクロールバー領域を調整する
      // 強制的にスクロールバーを表示させて幅を計算
      document.body.style.overflow = 'scroll';
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden'; // スクロールを無効化
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.documentElement.style.removeProperty('scrollbar-gutter');
    } else {
      context.handleDecrementOverlapLevel();
    }
  }, [isOpen]);

  useEffect(() => {
    closeConfirmModal();
  }, [location.pathname]);

  return (
    <ConfirmModalContext.Provider value={{ isOpen, openConfirmModal, closeConfirmModal }}>
      {children}
      <ConfirmModal isOpen={isOpen} modalComponent={modalComponent} />
    </ConfirmModalContext.Provider>
  );
};

export const useConfirmModal = () => {
  const context = useContext(ConfirmModalContext);
  if (!context) {
    throw new Error('ConfirmModalContext must be used within a XXXProvider');
  }
  return context;
};
